import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ml-auto"
}
const _hoisted_2 = { key: 1 }

import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import { TreeNode } from 'primevue/treenode';
import { useToast } from 'primevue/usetoast';
import { OrganizationInterface, OrganizationsInterface, OrganizationTableInterface } from '@/types';
import AddOrganizationModal from '@/components/AddOrganizationModal/AddOrganizationModal.vue';
import OrganizationConnections from './OrganizationConnections.vue';
import { useAuthStore, useClinicianOrganizationStore } from '@/store';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface OrganizationPropsInterface {
  tableData?: OrganizationInterface[];
  clinicianId?: number | undefined;
  flatOrganizations: TreeNode[];
  loading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ClinicianOrganizationTable',
  props: {
    tableData: {},
    clinicianId: {},
    flatOrganizations: {},
    loading: { type: Boolean }
  },
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const authStore = useAuthStore();
const organizationClinicianStore = useClinicianOrganizationStore();
const organizations: Ref<OrganizationInterface[] | undefined> = toRef(props, 'tableData');
const flatOrganizations = toRef(props, 'flatOrganizations');
const organizationCount: Ref<number> = computed(() => (organizations.value ? organizations.value.length : 0));
const organizationModalOpen = ref(false);
const toast = useToast();
const organizationItems: Ref<OrganizationsInterface[]> = ref([]);
const addEditOrgsButtonText = computed<string>(() =>
  organizations.value?.length ? 'Edit Organizations' : 'Add Organizations',
);

const createMenuModel = (item: OrganizationTableInterface) => {
  if (authStore.currentInstanceIsMasterTenant || !can([PERMISSIONS.CLINICIAN_DETACH_ORGANIZATIONS])) {
    return [];
  }

  const last = item[item.length - 1];
  const id = last.id;
  const name = last.name;
  return [
    {
      label: 'Remove Organization',
      command: () => detachOrganization([id], name),
    },
  ];
};

const attachOrganization: (ids: number[]) => Promise<void> = async (ids) => {
  await organizationClinicianStore.attachOrganizations({
    organizations: ids,
    clinicianId: props.clinicianId as number,
  });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Added',
    detail: 'Organization(s) have been added',
    life: 3000,
  });
};

const detachOrganization: (id: number[], organizationName?: string) => Promise<void> = async (
  ids,
  organizationName,
) => {
  await organizationClinicianStore.detachOrganizations({
    organizations: ids,
    clinicianId: props.clinicianId as number,
  });

  const detail = organizationName
    ? `Organization ${organizationName} has been removed from this Service Provider`
    : `Organization(s) have been removed from this Service Provider`;

  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Removed',
    detail,
    life: 3000,
  });
};

const openAddOrganizations = async () => {
  if (
    can([PERMISSIONS.CLINICIAN_ATTACH_ORGANIZATIONS]) &&
    can([PERMISSIONS.CLINICIAN_ORGANIZATIONS_AVAILABLE_TO_ADD])
  ) {
    organizationItems.value = await organizationClinicianStore.getAddOrganizationsModalData(
      props.clinicianId as number,
    );
    organizationModalOpen.value = true;
  } else {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'You do not have permission to add organizations',
      life: 3000,
    });
  }
};

const getUsersCurrentOrganizations = computed(() => {
  const selectedItems: Record<number, Record<string, boolean>> = {};
  const addOrgToObject = (organization: TreeNode) => {
    if (Array.isArray(organization)) organization = organization[0];
    selectedItems[Number(organization.id)] = { checked: true, partialChecked: false };
    if (organization.children?.length) organization.children.forEach(addOrgToObject);
  };
  flatOrganizations.value?.forEach(addOrgToObject);

  return selectedItems;
});

const onCancelAdd = () => (organizationModalOpen.value = false);

const onSaveAdd = (organizations: number[]) => {
  const previousOrganizationIds = Object.keys(getUsersCurrentOrganizations.value).map((value) => Number(value));
  const organizationsToAttach = organizations.filter((organization) => !previousOrganizationIds.includes(organization));
  const organizationsToDetach = previousOrganizationIds.filter((organization) => !organizations.includes(organization));
  if (organizationsToDetach.length) detachOrganization(organizationsToDetach);
  if (organizationsToAttach.length) attachOrganization(organizationsToAttach);
  organizationModalOpen.value = false;
};

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Column = _resolveComponent("Column")!
  const _component_ActionMenu = _resolveComponent("ActionMenu")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!

  return (_openBlock(), _createBlock(_component_Card, null, {
    header: _withCtx(() => [
      _cache[0] || (_cache[0] = _createElementVNode("h2", null, "Organizations view", -1)),
      _createVNode(_unref(Chip), null, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(organizationCount.value), 1)
        ]),
        _: 1
      }),
      (!_unref(authStore).currentInstanceIsMasterTenant && _unref(can)([_unref(PERMISSIONS).CLINICIAN_ATTACH_ORGANIZATIONS]))
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_AppButton, {
              type: "text",
              size: "small",
              text: addEditOrgsButtonText.value,
              "icon-left": "add",
              onClick: openAddOrganizations
            }, null, 8, ["text"])
          ]))
        : _createCommentVNode("", true)
    ]),
    content: _withCtx(() => [
      _createVNode(_component_DataTable, {
        value: organizations.value,
        "global-filter-fields": ['name'],
        scrollable: true,
        "loading-icon": "logo-spinner",
        loading: __props.loading,
        "data-key": "name",
        "responsive-layout": "scroll",
        "scroll-height": "440px",
        "state-key": "session-clinician-organization-store-table",
        "state-storage": "session",
        class: _normalizeClass({ 'table-loading': __props.loading })
      }, {
        empty: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode("Organizations not assigned", -1)
        ]))]),
        default: _withCtx(() => [
          (organizationCount.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_Column, {
                  "body-class": "w-full",
                  "header-class": "w-full",
                  field: "name",
                  header: "Organization"
                }, {
                  body: _withCtx(({ data }) => [
                    _createVNode(OrganizationConnections, { organizations: data }, null, 8, ["organizations"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  "body-class": "justify-content-center mw-10rem",
                  "header-class": "justify-content-center mw-10rem",
                  field: "id",
                  header: "Actions"
                }, {
                  body: _withCtx(({ data }) => [
                    (createMenuModel(data).length)
                      ? (_openBlock(), _createBlock(_component_ActionMenu, {
                          key: 0,
                          "menu-items": createMenuModel(data),
                          icon: "more_horiz"
                        }, null, 8, ["menu-items"]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_2, "-"))
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["value", "loading", "class"]),
      (organizationModalOpen.value && _unref(can)([_unref(PERMISSIONS).CLINICIAN_ORGANIZATIONS_AVAILABLE_TO_ADD]))
        ? (_openBlock(), _createBlock(AddOrganizationModal, {
            key: 0,
            title: addEditOrgsButtonText.value,
            items: organizationItems.value,
            searchable: true,
            "multi-selection": true,
            "selected-items-data": getUsersCurrentOrganizations.value,
            onCancel: onCancelAdd,
            onSave: onSaveAdd
          }, null, 8, ["title", "items", "selected-items-data"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})